export interface RiskPictureUpdated {
  at: Date;
  /** Comment author when the payload has one, otherwise the snapshot producer. */
  source?: string;
}

const DATE_KEYS = ["version_dt", "updated_at", "generated_at"] as const;

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null && !Array.isArray(value);

const parseDate = (value: unknown): Date | undefined => {
  if (typeof value !== "string") return undefined;
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? undefined : date;
};

const personName = (value: unknown): string | undefined => {
  if (!isRecord(value) || typeof value.name !== "string" || !value.name.trim()) {
    return undefined;
  }
  return value.name;
};

const pushDates = (dates: Date[], record: Record<string, unknown>) => {
  for (const key of DATE_KEYS) {
    const date = parseDate(record[key]);
    if (date) dates.push(date);
  }
};

/**
 * Newest timestamp on the snapshot. Part `version_dt` wins over the heartbeat
 * `generated_at`, which is not always a parseable date.
 */
export const riskPictureUpdated = (data: unknown): RiskPictureUpdated | undefined => {
  const dates: Date[] = [];
  let source: string | undefined;

  const visitWarning = (warning: unknown) => {
    if (!isRecord(warning)) return;
    pushDates(dates, warning);
    if (!Array.isArray(warning.parts)) return;
    for (const part of warning.parts) {
      if (isRecord(part)) pushDates(dates, part);
    }
  };

  if (Array.isArray(data)) {
    data.forEach(visitWarning);
  } else if (isRecord(data)) {
    if (isRecord(data.metadata)) {
      pushDates(dates, data.metadata);
      source = personName(data.metadata.producer) ?? source;
    }
    if (isRecord(data.comment)) {
      pushDates(dates, data.comment);
      source = personName(data.comment.updated_by) ?? source;
    }
    if (Array.isArray(data.warnings)) data.warnings.forEach(visitWarning);
    else visitWarning(data);
  }

  const at = dates.sort((a, b) => b.getTime() - a.getTime())[0];
  if (!at) return undefined;
  return source ? { at, source } : { at };
};
